import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Mail, KeyRound, ArrowLeft, User, ShieldCheck, Phone } from 'lucide-react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { appleSignInPopup } from '../lib/apple';

const GOOGLE_CLIENT_ID = '1074860921211-q5gllqc42prmmq7kjdt7s1qiv014srb8.apps.googleusercontent.com';

// V14.5 Part 17 — capture browser TZ once after auth, fire-and-forget.
// Backend uses this to format outbound WATI/email booking confirmations
// in the learner's local time instead of raw IST. Failure is silent.
// V63.7 — split "+918469601350" into ["+91", "8469601350"] using the real ITU dial-code
// lengths. The old greedy regex took "+918" as the country code, so the backend searched
// for a 9-digit national number, missed the existing account and created a duplicate.
const DIAL_1 = new Set(['1', '7']);
const DIAL_2 = new Set(['20', '27', '30', '31', '32', '33', '34', '36', '39', '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58', '60', '61', '62', '63', '64', '65', '66', '81', '82', '84', '86', '90', '91', '92',
  '93', '94', '95', '98']);
export function splitDialCode(raw) {
  const s = (raw || '').replace(/[^\d+]/g, '');
  if (!s.startsWith('+')) return null;
  const digits = s.slice(1);
  let cc = digits.slice(0, 3);
  if (DIAL_1.has(digits.slice(0, 1))) cc = digits.slice(0, 1);
  else if (DIAL_2.has(digits.slice(0, 2))) cc = digits.slice(0, 2);
  let national = digits.slice(cc.length).replace(/^0+/, '');
  if (national.length < 6 || national.length > 14) return null;
  return { countryCode: `+${cc}`, national };
}

function sendBrowserTimezone() {
  try {
    const tz = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone;
    if (tz) api.put('/api/users/me/timezone', { timezone: tz }).catch(() => { });
  } catch { /* noop */ }
}

function LoginForm() {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('signin'); // signin | signup
  // V14.5 Part 19f — added two new steps for email-OTP verification:
  //   `verify_email_otp` (sign-up OTP entry) and `forgot_no_account`
  //   (V19e). Existing flow keys are unchanged.
  const [step, setStep] = useState('choose'); // choose | password | forgot | forgot_sent | forgot_no_account | verify_email_otp
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // V14.5 Part 17 — email-password Sign Up (for users without Google)
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  // V45 — email quality check (typos / throwaway / generic inboxes)
  const [emailIssue, setEmailIssue] = useState(null); // {code, message, suggestion}
  const [emailChecking, setEmailChecking] = useState(false);
  // V14.5 Part 19f — email-OTP signup verification
  const [signupOtp, setSignupOtp] = useState('');
  const [otpCooldown, setOtpCooldown] = useState(0);
  // V14.5 Part 19g — phone-OTP chain after email verification
  const [signupPhone, setSignupPhone] = useState('+91');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpCooldown, setPhoneOtpCooldown] = useState(0);
  // Jun 2026 — phone-OTP LOGIN on web (sign-in) + Apple web sign-in.
  const [loginPhone, setLoginPhone] = useState('+91');
  const [loginPhoneOtp, setLoginPhoneOtp] = useState('');
  const [loginPhoneMasked, setLoginPhoneMasked] = useState('');
  const [loginPhoneCooldown, setLoginPhoneCooldown] = useState(0);
  const navigate = useNavigate();
  const { setAuth, user } = useAuth();
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  // OPS V20 §5.1 — payment-return fallback: /login?reason=<code>&email=<prefill>
  const reason = searchParams.get('reason');
  const emailParam = searchParams.get('email');
  const REASON_MESSAGES = {
    sso_unavailable: 'Your payment is confirmed. Auto sign-in is unavailable right now — please log in with your email below.',
    sso_expired: 'That sign-in link has expired. Please log in with your email below.',
    token_expired: 'That sign-in link has expired. Please log in with your email below.',
    token_consumed: 'That sign-in link was already used. Please log in below.',
    token_not_found: 'That sign-in link is not valid anymore. Please log in below.',
    otp_required: 'Please log in to verify your mobile number and continue.',
    session_expired: 'Your session expired. Please log in again.',
    invalid_signature: 'We could not verify that payment return. If you have paid, your access is being activated — log in below to continue.',
  };
  const reasonMessage = reason ? (REASON_MESSAGES[reason] || 'Please log in to continue.') : null;
  useEffect(() => {
    if (emailParam && !email) { setEmail(emailParam); setStep('password'); }
  }, [emailParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // V14.5 Part 19f — Inverse auth-guard: if an already-authenticated user
  // lands on /login (typically via browser-back from their dashboard), bounce
  // them straight back to their dashboard. Pre-fix: back-button left them on
  // the sign-in screen even though they had a valid session — confusing UX.
  useEffect(() => {
    if (user && user.role) {
      const dest =
        user.role === 'superadmin' || user.role === 'admin' ? '/admin/dashboard'
        : user.role === 'instructor' ? '/instructor/dashboard'
        : user.role === 'doctor' ? (user.medical_category === 'nutritionist' ? '/doctor/medical-inbox' : '/doctor/dashboard')
        : '/learner/dashboard';
      navigate(dest, { replace: true });
    }
  }, [user, navigate]);

  // V14.5 Part 19f — Resend-OTP cooldown ticker. Prevents tap-spam DOS-ing
  // our Gmail SMTP and helps moms see when the resend is available again.
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  // V14.5 Part 19g — Same ticker for the WhatsApp phone-OTP resend cooldown.
  useEffect(() => {
    if (phoneOtpCooldown <= 0) return;
    const t = setTimeout(() => setPhoneOtpCooldown(phoneOtpCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [phoneOtpCooldown]);

  // Ticker for the phone-OTP LOGIN resend cooldown (sign-in flow).
  useEffect(() => {
    if (loginPhoneCooldown <= 0) return;
    const t = setTimeout(() => setLoginPhoneCooldown(loginPhoneCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [loginPhoneCooldown]);

  const routeByRole = (role, medicalCategory) => {
    if (role === 'superadmin' || role === 'admin') return '/admin/dashboard';
    if (role === 'instructor') return '/instructor/dashboard';
    if (role === 'doctor') return medicalCategory === 'nutritionist' ? '/doctor/medical-inbox' : '/doctor/dashboard';
    return '/learner/dashboard';
  };

  const finishLogin = (data, greeting) => {
    // V14.5 Part 17 — direct setAuth (eliminates the second /me round-trip
    // that was the root cause of the "black screen post-OTP" reports).
    setAuth({ token: data.access_token, user: data.user });
    sendBrowserTimezone();
    toast.success(greeting);
    setTimeout(() => {
      try {
        navigate(routeByRole(data.user.role, data.user.medical_category), { replace: true });
      } catch {
        window.location.href = routeByRole(data.user.role, data.user.medical_category);
      }
    }, 50);
  };

  // Sign in with Apple (web popup) — posts the identity token to the same
  // backend endpoint the iOS app uses. Backend creates/links the user and
  // returns {access_token, user}.
  const handleAppleSignIn = async () => {
    setLoading(true);
    try {
      const { identityToken, fullName } = await appleSignInPopup();
      const res = await api.post('/api/auth/apple/verify-token', {
        identity_token: identityToken,
        full_name: fullName,
      });
      finishLogin(res.data, `Welcome${res.data.is_new ? '' : ' back'}, ${res.data.user.name || 'Mom-to-be'}!`);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (err?.message?.includes('popup') ? 'Apple sign-in was cancelled.' : null)
        || 'Apple sign-in is not available right now. Please use another method.';
      toast.error(typeof msg === 'string' ? msg : 'Apple sign-in failed.');
      setLoading(false);
    }
  };

  // Phone-OTP LOGIN (sign-in) — step 1: request a WhatsApp OTP.
  const handleSendLoginOtp = async (e) => {
    if (e) e.preventDefault();
    const phone = (loginPhone || '').replace(/\s+/g, '');
    const m = splitDialCode(phone);
    if (!m) { toast.error('Enter your number with country code, e.g. +91 98765 43210'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/phone/send-login-otp', {
        phone: m.national, country_code: m.countryCode,
      });
      setLoginPhoneMasked(res.data.phone_masked || phone);
      setLoginPhoneCooldown(res.data.resend_available_in || 30);
      setStep('phone_login_otp');
      toast.success('OTP sent on WhatsApp');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send OTP. Try again.');
    } finally {
      setLoading(false);
    }
  };

  // Phone-OTP LOGIN (sign-in) — step 2: verify OTP and sign in.
  const handleVerifyLoginOtp = async (e) => {
    if (e) e.preventDefault();
    const phone = (loginPhone || '').replace(/\s+/g, '');
    const m = splitDialCode(phone);
    const otp = (loginPhoneOtp || '').trim();
    if (!m || otp.length !== 6) { toast.error('Enter the 6-digit code.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/phone/verify-login-otp', {
        phone: m.national, country_code: m.countryCode, otp,
      });
      if (res.data.needs_profile_completion) {
        setAuth({ token: res.data.access_token, user: res.data.user });
        sendBrowserTimezone();
        navigate('/learner/dashboard', { replace: true });
      } else {
        finishLogin(res.data, `Welcome back, ${res.data.user.name || 'Mom-to-be'}!`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid or expired OTP.');
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    try {
      const res = await api.post('/api/auth/google/verify-token', {
        credential: credentialResponse.credential,
      });
      if (res.data.is_new) {
        const params = new URLSearchParams({
          email: res.data.email, name: res.data.name,
          picture: res.data.picture || '', google_id: res.data.google_id || '',
        });
        navigate(`/auth/register?${params.toString()}`);
      } else {
        finishLogin(res.data, `Welcome back, ${res.data.user.name}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Authentication failed.');
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const res = await api.post('/api/auth/login', { email: email.trim().toLowerCase(), password });
      finishLogin(res.data, `Welcome back, ${res.data.user.name}!`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Differentiate "wrong password" vs "no account" vs "OPS-created
        // account that has no password yet" (V14.5 Part 19o — these users
        // came in via trial-class purchase webhook; their only path in
        // is via "Forgot password" to set a credential, or Google).
        try {
          const ex = await api.get(`/api/auth/account-exists?email=${encodeURIComponent(email.trim())}`);
          if (!ex.data.exists) {
            toast.error('No account with this email — please sign up first.');
            setMode('signup');
            setStep('choose');
          } else if (ex.data.needs_password_setup) {
            // OPS-provisioned: pre-payment account with no credential set.
            // Route to the in-place Forgot Password flow so they can set
            // a password via the email reset link.
            toast.info(
              "Your account exists but doesn't have a password yet — " +
              "we just sent you a link to set one up."
            );
            setStep('forgot');
            // Auto-submit the forgot-password form for them.
            setTimeout(() => {
              const form = document.querySelector('[data-testid="forgot-form"]');
              if (form) form.requestSubmit();
            }, 100);
          } else if (ex.data.has_google && !ex.data.has_password) {
            toast.error('This account was created with Google — please tap "Continue with Google" above.');
          } else {
            toast.error('Incorrect password. Try again or use "Forgot password".');
          }
        } catch {
          toast.error('Sign-in failed. Please try again.');
        }
      } else {
        toast.error(err?.response?.data?.detail || 'Sign-in failed.');
      }
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      // V14.5 Part 19e — response now carries {sent, has_password,
      // has_google, is_google_only}. We keep `forgot_sent` UX simple
      // (just "check your email") regardless — the nuance is already
      // baked into the email's subject + copy server-side.
      await api.post('/api/auth/forgot-password', { email: email.trim().toLowerCase() });
      setStep('forgot_sent');
    } catch (err) {
      // V14.5 Part 19e — explicit 404 NO_ACCOUNT case so mom knows
      // her email isn't in the system (instead of waiting for an email
      // that will never come). Anti-enumeration was traded for UX.
      const data = err?.response?.data || {};
      const code = data.detailObject?.code || null;
      if (code === 'NO_ACCOUNT') {
        setStep('forgot_no_account');
      } else {
        toast.error(data.detail || 'Could not send reset link. Try again.');
      }
    }
    setLoading(false);
  };

  // V14.5 Part 17 — Email/password Sign Up for users without a Google account.
  // Backend `/api/auth/register` is gated to role=learner and validates
  // password ≥ 6 chars + email uniqueness, returns the same {access_token, user}
  // shape as Google register so we reuse finishLogin().
  // V45 — pre-check the typed email against the server's email-quality rules
  // (syntax, typo suggestions, disposable domains, generic inboxes, dead
  // domains). Runs on blur so the mom sees the problem before she submits.
  const checkSignupEmail = async (raw) => {
    const em = (raw || '').trim().toLowerCase();
    if (!em || !em.includes('@')) { setEmailIssue(null); return null; }
    setEmailChecking(true);
    try {
      const res = await api.post('/api/auth/validate-email', { email: em });
      const v = res.data || {};
      if (v.valid) { setEmailIssue(null); return null; }
      const issue = { code: v.code, message: v.message, suggestion: v.suggestion || null };
      setEmailIssue(issue);
      return issue;
    } catch {
      setEmailIssue(null); // never block signup on a failed pre-check
      return null;
    } finally {
      setEmailChecking(false);
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    const name = signupName.trim();
    const em = signupEmail.trim().toLowerCase();
    if (!name) { toast.error('Please enter your name.'); return; }
    if (!em) { toast.error('Please enter your email.'); return; }
    if (signupPassword.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
    const issue = await checkSignupEmail(em);
    if (issue) { toast.error(issue.message); return; }
    setLoading(true);
    try {
      // V14.5 Part 19f — Step 1 of email-OTP signup.
      // V14.5 Part 19g — also pass phone so backend knows to chain into
      // phone-OTP verification after email is confirmed.
      const res = await api.post('/api/auth/email-otp/send', {
        name,
        email: em,
        password: signupPassword,
        phone: signupPhone.trim() || null,
      });
      toast.success(`We've emailed a 6-digit code to ${em}. Check spam if you don't see it.`);
      setOtpCooldown(res?.data?.resend_available_in_seconds || 60);
      setSignupOtp('');
      setStep('verify_email_otp');
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.detailObject?.code || null;
      const masked = data.detailObject?.existing_email_masked || null;
      const msg = data.detail || 'Sign-up failed. Please try again.';
      if (code === 'EMAIL_EXISTS') {
        // Email already exists — switch to Sign In and prefill the email.
        toast.error(msg);
        setMode('signin');
        setStep('choose');
        setEmail(em);
      } else if (code === 'PHONE_EXISTS') {
        // Phone matches a different email. Backend now returns the masked
        // owner email so we can route the user to the right account.
        toast.error(msg, { duration: 8000 });
        setMode('signin');
        setStep('choose');
        if (masked) {
          // Prefill the email field with the masked value so the user sees
          // "this is the account I need to sign into". They'll type the
          // actual email — masking is just a hint.
          setEmail('');
        }
      } else if (['EMAIL_INVALID_FORMAT', 'EMAIL_DISPOSABLE', 'EMAIL_GENERIC_ADDRESS',
                  'EMAIL_TYPO_SUSPECTED', 'EMAIL_DOMAIN_UNREACHABLE', 'EMAIL_REQUIRED'].includes(code)) {
        // V45 — email quality gate rejected the address server-side.
        setEmailIssue({ code, message: msg, suggestion: data.detailObject?.suggestion || null });
        toast.error(msg, { duration: 7000 });
      } else if (code === 'OTP_RESEND_COOLDOWN') {
        // The server says a code was very recently sent — drop the user
        // onto the OTP screen so they can use the existing one.
        toast.info(msg);
        setOtpCooldown(data.detailObject?.retry_after || 30);
        setStep('verify_email_otp');
      } else {
        toast.error(msg);
      }
    }
    setLoading(false);
  };

  // V14.5 Part 19f — Step 2: verify the typed-in code, create the account,
  // and chain into phone-OTP if the user gave a phone number.
  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    const otp = signupOtp.trim();
    if (!otp || otp.length < 4) {
      toast.error('Please enter the 6-digit code from your email.');
      return;
    }
    const em = signupEmail.trim().toLowerCase();
    setLoading(true);
    try {
      const res = await api.post('/api/auth/email-otp/verify', { email: em, otp });
      const data = res.data || {};
      // V14.5 Part 19g — if we have an unverified phone, chain into the
      // WhatsApp OTP step BEFORE finishing login (no dashboard yet).
      if (data.needs_phone_verification && signupPhone.trim().length > 6) {
        // Persist the access_token so the next step's authenticated call
        // (POST /signup-verify-phone) can use it.
        if (data.access_token) localStorage.setItem('token', data.access_token);
        try {
          await api.post('/api/auth/send-otp', { phone: signupPhone.trim() });
          toast.success('Email verified. We just sent a WhatsApp code to your phone.');
          setPhoneOtpCooldown(60);
          setPhoneOtp('');
          setStep('verify_phone_otp');
        } catch {
          // WhatsApp delivery failure shouldn't block onboarding — let
          // the user into the dashboard; they can verify phone in profile.
          toast.warning("We couldn't send the WhatsApp code — you can verify your phone later from Profile.");
          finishLogin(data, `Welcome to Soul Sanskar, ${signupName.split(' ')[0]}!`);
        }
        setLoading(false);
      } else {
        finishLogin(data, `Welcome to Soul Sanskar, ${signupName.split(' ')[0]}!`);
      }
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.detailObject?.code || null;
      const msg = data.detail || 'Verification failed.';
      if (code === 'OTP_EXPIRED' || code === 'PENDING_NOT_FOUND' || code === 'OTP_LOCKED') {
        toast.error(msg);
        setStep('choose');
      } else {
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  // V14.5 Part 19g — Phone-OTP verify step. Authenticated call — uses the
  // access_token stored after email-OTP success. On 200, we hydrate auth
  // context with the refreshed user (phone_verified=true) and land them
  // on their dashboard.
  const handleVerifyPhoneOtp = async (e) => {
    e.preventDefault();
    const otp = phoneOtp.trim();
    if (!otp || otp.length < 4) {
      toast.error('Please enter the 6-digit WhatsApp code.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/api/auth/signup-verify-phone', {
        phone: signupPhone.trim(),
        otp,
      });
      const verified = res.data?.user;
      const stored = localStorage.getItem('token');
      finishLogin(
        { access_token: stored, user: verified },
        `All set, ${signupName.split(' ')[0]}! 🎉`,
      );
    } catch (err) {
      const data = err?.response?.data || {};
      toast.error(data.detail || 'Incorrect or expired code.');
      setLoading(false);
    }
  };

  const handleSkipPhoneOtp = () => {
    const stored = localStorage.getItem('token');
    if (!stored) {
      toast.error('Session expired. Please sign in again.');
      setStep('choose');
      return;
    }
    finishLogin(
      { access_token: stored, user: null },  // finishLogin re-fetches /me
      `Welcome to Soul Sanskar, ${signupName.split(' ')[0]}!`,
    );
  };

  const handleResendPhoneOtp = async () => {
    if (phoneOtpCooldown > 0) return;
    setLoading(true);
    try {
      await api.post('/api/auth/send-otp', { phone: signupPhone.trim() });
      toast.success('New WhatsApp code sent.');
      setPhoneOtpCooldown(60);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend code.');
    }
    setLoading(false);
  };

  // V14.5 Part 19f — Resend a fresh OTP. Server enforces a 60s cooldown
  // and rotates the stored hash (old code becomes invalid).
  const handleResendOtp = async () => {
    if (otpCooldown > 0) return;
    const name = signupName.trim();
    const em = signupEmail.trim().toLowerCase();
    setLoading(true);
    try {
      const res = await api.post('/api/auth/email-otp/send', {
        name,
        email: em,
        password: signupPassword,
      });
      toast.success('New code sent.');
      setOtpCooldown(res?.data?.resend_available_in_seconds || 60);
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.detailObject?.code === 'OTP_RESEND_COOLDOWN') {
        setOtpCooldown(data.detailObject?.retry_after || 30);
        toast.info(data.detail);
      } else {
        toast.error(data.detail || 'Could not resend code.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 h-24 w-24 rounded-2xl overflow-hidden bg-white flex items-center justify-center shadow-sm border">
            <img src="/logo.png" alt="Soul Sanskar" className="h-32 w-32 object-contain scale-150" />
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-foreground">Soul Sanskar</h1>
          <p className="text-muted-foreground mt-1">Academy</p>
        </div>

        {/* Sign In / Sign Up Toggle */}
        {step === 'choose' && (
          <div className="flex bg-muted rounded-xl p-1 mb-4" data-testid="login-mode-toggle">
            <button onClick={() => setMode('signin')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === 'signin' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`} data-testid="signin-toggle">
              Sign In
            </button>
            <button onClick={() => setMode('signup')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${mode === 'signup' ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground'}`} data-testid="signup-toggle">
              Sign Up
            </button>
          </div>
        )}

        <Card className="rounded-2xl border bg-white shadow-sm">
          <CardContent className="pt-6 pb-6">
            {error === 'auth_failed' && (
              <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200">
                <p className="text-xs text-rose-800">Authentication failed. Please try again.</p>
              </div>
            )}
            {reasonMessage && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="login-reason-banner">
                <p className="text-xs text-amber-900">{reasonMessage}</p>
              </div>
            )}

            {/* === Forgot password — no account exists (V14.5 Part 19e) === */}
            {step === 'forgot_no_account' ? (
              <>
                <h2 className="font-heading text-xl font-semibold mb-1" data-testid="forgot-no-account-heading">No account found</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  We couldn't find an account for <strong>{email}</strong>. Please check the spelling, or sign up to create a new account.
                </p>
                <div className="space-y-2">
                  <Button
                    className="w-full"
                    onClick={() => { setMode('signup'); setStep('choose'); setSignupEmail(email); }}
                    data-testid="forgot-no-account-signup-btn"
                  >
                    Create an account
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { setStep('forgot'); }}
                    data-testid="forgot-no-account-try-again-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Try a different email
                  </Button>
                </div>
              </>
            ) : step === 'forgot_sent' ? (
              <>
                <h2 className="font-heading text-xl font-semibold mb-1">Check your email</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  We've sent a password reset link to <strong>{email}</strong>. The link expires in 30 minutes. If you originally signed up with Google, this email lets you ALSO set a password — both methods will work after.
                </p>
                <Button variant="outline" className="w-full" onClick={() => { setStep('choose'); setEmail(''); }} data-testid="back-to-signin">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sign In
                </Button>
              </>
            ) : step === 'forgot' ? (
              /* === Forgot password — email entry === */
              <>
                <h2 className="font-heading text-xl font-semibold mb-1">Reset password</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  Enter your account email — we'll send a reset link.
                </p>
                <form onSubmit={handleForgot} className="space-y-3">
                  <div>
                    <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label>
                    <Input id="forgot-email" type="email" autoFocus value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      data-testid="forgot-email-input" required />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="send-reset-link-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send reset link <Mail className="h-4 w-4 ml-2" /></>}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => setStep('choose')}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                </form>
              </>
            ) : step === 'phone_login' ? (
              /* === Phone-OTP login — number entry === */
              <>
                <h2 className="font-heading text-xl font-semibold mb-1">Login with phone</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  We'll send a 6-digit code to your WhatsApp.
                </p>
                <form onSubmit={handleSendLoginOtp} className="space-y-3">
                  <div>
                    <Label htmlFor="login-phone" className="text-xs uppercase tracking-wide text-muted-foreground">Phone (with country code)</Label>
                    <Input id="login-phone" type="tel" autoFocus value={loginPhone}
                      onChange={(e) => setLoginPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      autoComplete="tel"
                      data-testid="login-phone-input" required />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="send-login-otp-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send code <Phone className="h-4 w-4 ml-2" /></>}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full" onClick={() => { setStep('choose'); setLoginPhoneOtp(''); }} data-testid="phone-login-back">
                    <ArrowLeft className="h-4 w-4 mr-2" /> Back
                  </Button>
                </form>
              </>
            ) : step === 'phone_login_otp' ? (
              /* === Phone-OTP login — code entry === */
              <>
                <h2 className="font-heading text-xl font-semibold mb-1">Enter your code</h2>
                <p className="text-sm text-muted-foreground mb-5">
                  We sent a 6-digit code on WhatsApp to <strong>{loginPhoneMasked}</strong>.
                </p>
                <form onSubmit={handleVerifyLoginOtp} className="space-y-3">
                  <div>
                    <Label htmlFor="login-phone-otp" className="text-xs uppercase tracking-wide text-muted-foreground">6-digit code</Label>
                    <Input id="login-phone-otp" inputMode="numeric" maxLength={6} autoFocus value={loginPhoneOtp}
                      onChange={(e) => setLoginPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="••••••"
                      className="tracking-[0.5em] text-center text-lg"
                      data-testid="login-phone-otp-input" required />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="verify-login-otp-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-2" /> Verify &amp; sign in</>}
                  </Button>
                  <div className="flex items-center justify-between">
                    <Button type="button" variant="ghost" size="sm" onClick={() => { setStep('phone_login'); setLoginPhoneOtp(''); }} data-testid="phone-login-otp-back">
                      <ArrowLeft className="h-4 w-4 mr-1" /> Change number
                    </Button>
                    <Button type="button" variant="ghost" size="sm" disabled={loginPhoneCooldown > 0}
                      onClick={handleSendLoginOtp} data-testid="phone-login-resend">
                      {loginPhoneCooldown > 0 ? `Resend in ${loginPhoneCooldown}s` : 'Resend code'}
                    </Button>
                  </div>
                </form>
              </>
            ) : mode === 'signin' ? (
              /* === Sign In === */
              <>
                <h2 className="font-heading text-xl font-semibold mb-1">Welcome back</h2>
                <p className="text-sm text-muted-foreground mb-5">Choose how you'd like to sign in</p>

                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">Signing in…</span>
                  </div>
                ) : (
                  <>
                    {/* Google */}
                    <div className="flex justify-center" data-testid="google-login-button">
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error('Google sign-in cancelled.')}
                        size="large" width="320" text="signin_with" shape="rectangular" theme="outline" />
                    </div>

                    {/* Apple (web) */}
                    <Button type="button" variant="outline"
                      onClick={handleAppleSignIn}
                      className="w-full mt-2 h-[40px] border-slate-300 bg-black text-white hover:bg-black/90 hover:text-white"
                      data-testid="apple-login-button">
                      <svg viewBox="0 0 384 512" className="h-4 w-4 mr-2 fill-current" aria-hidden="true">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                      </svg>
                      Sign in with Apple
                    </Button>

                    {/* Phone OTP login */}
                    <Button type="button" variant="outline"
                      onClick={() => setStep('phone_login')}
                      className="w-full mt-2 h-[40px] border-slate-300"
                      data-testid="phone-login-option-button">
                      <Phone className="h-4 w-4 mr-2" /> Continue with phone number
                    </Button>

                    <div className="flex items-center gap-2 my-5">
                      <Separator className="flex-1" />
                      <span className="text-xs text-muted-foreground">OR</span>
                      <Separator className="flex-1" />
                    </div>

                    {/* Email + password */}
                    <form onSubmit={handlePasswordSubmit} className="space-y-3" data-testid="email-password-form">
                      <div>
                        <Label htmlFor="email" className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label>
                        <Input id="email" type="email" value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          data-testid="login-email-input" required />
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <Label htmlFor="password" className="text-xs uppercase tracking-wide text-muted-foreground">Password</Label>
                          <button type="button" onClick={() => setStep('forgot')} className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                            Forgot?
                          </button>
                        </div>
                        <Input id="password" type="password" value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          data-testid="login-password-input" required />
                      </div>
                      <Button type="submit" className="w-full" data-testid="login-submit-button">
                        <KeyRound className="h-4 w-4 mr-2" /> Sign in with email
                      </Button>
                    </form>

                    <p className="text-xs text-muted-foreground text-center mt-4">
                      Don't have an account?{' '}
                      <button type="button" onClick={() => setMode('signup')} className="text-primary hover:underline">Sign up</button>
                    </p>
                  </>
                )}
              </>
            ) : (
              /* === Sign Up === */
              <>
                {step === 'verify_phone_otp' ? (
                  /* === V14.5 Part 19g — Phone OTP after email verification === */
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="h-5 w-5 text-primary" />
                      <h2 className="font-heading text-xl font-semibold">Verify your phone (WhatsApp)</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      Email verified ✓. We've sent a 6-digit code via <strong>WhatsApp</strong> to <strong>{signupPhone}</strong>. Enter it below to finish.
                    </p>
                    <p className="text-xs text-muted-foreground mb-5 bg-secondary/40 border border-border rounded-md px-3 py-2">
                      💬 We use WhatsApp for class reminders, booking confirmations, and important updates. Verifying lets you receive all of these where you already are.
                    </p>
                    <form onSubmit={handleVerifyPhoneOtp} className="space-y-3" data-testid="verify-phone-otp-form">
                      <div>
                        <Label htmlFor="phone-otp" className="text-xs uppercase tracking-wide text-muted-foreground">6-digit code</Label>
                        <Input
                          id="phone-otp"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={phoneOtp}
                          onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="••••••"
                          autoComplete="one-time-code"
                          autoFocus
                          className="text-center text-2xl tracking-[0.5em] font-mono"
                          data-testid="phone-otp-input"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={loading || phoneOtp.length < 6}
                        data-testid="verify-phone-otp-submit-button"
                      >
                        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                        Verify phone & continue
                      </Button>
                      <button
                        type="button"
                        onClick={handleResendPhoneOtp}
                        disabled={phoneOtpCooldown > 0 || loading}
                        className={`w-full text-center text-xs py-1 ${phoneOtpCooldown > 0 ? 'text-muted-foreground cursor-not-allowed' : 'text-primary hover:underline'}`}
                        data-testid="phone-otp-resend-button"
                      >
                        {phoneOtpCooldown > 0 ? `Resend WhatsApp code in ${phoneOtpCooldown}s` : "Didn't get the code? Resend"}
                      </button>
                    </form>
                    {/* V14.5 Part 19g — Explicit skip CTA so moms who prefer
                        not to use WhatsApp can opt out entirely. They land on
                        the dashboard with phone_verified=false; can verify
                        later from Profile, or just live without WhatsApp
                        notifications (we'll fall back to email reminders for
                        these users — see backend `_send_reminder` logic). */}
                    <div className="border-t border-border mt-5 pt-4">
                      <p className="text-xs text-muted-foreground mb-2">
                        Prefer not to use WhatsApp? You can skip this step.
                      </p>
                      <button
                        type="button"
                        onClick={handleSkipPhoneOtp}
                        disabled={loading}
                        className="text-xs text-muted-foreground hover:text-foreground underline"
                        data-testid="phone-otp-skip-button"
                      >
                        Skip WhatsApp & continue without phone notifications
                      </button>
                    </div>
                  </>
                ) : step === 'verify_email_otp' ? (
                  /* === V14.5 Part 19f — Email OTP verification step === */
                  <>
                    <div className="flex items-center gap-2 mb-1">
                      <ShieldCheck className="h-5 w-5 text-primary" />
                      <h2 className="font-heading text-xl font-semibold">Verify your email</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mb-5">
                      We sent a 6-digit code to <strong>{signupEmail}</strong>. Enter it below to finish creating your account.
                    </p>
                    <form onSubmit={handleVerifyEmailOtp} className="space-y-3" data-testid="verify-email-otp-form">
                      <div>
                        <Label htmlFor="signup-otp" className="text-xs uppercase tracking-wide text-muted-foreground">6-digit code</Label>
                        <Input
                          id="signup-otp"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={6}
                          value={signupOtp}
                          onChange={(e) => setSignupOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="••••••"
                          autoComplete="one-time-code"
                          autoFocus
                          className="text-center text-2xl tracking-[0.5em] font-mono"
                          data-testid="signup-otp-input"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={loading || signupOtp.length < 6}
                        data-testid="verify-otp-submit-button"
                      >
                        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                        Verify & create account
                      </Button>
                    </form>
                    <div className="flex items-center justify-between mt-4 text-xs">
                      <button
                        type="button"
                        onClick={() => { setStep('choose'); setSignupOtp(''); }}
                        className="text-muted-foreground hover:text-foreground inline-flex items-center"
                        data-testid="otp-back-button"
                      >
                        <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Change email
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={otpCooldown > 0 || loading}
                        className={`${otpCooldown > 0 ? 'text-muted-foreground cursor-not-allowed' : 'text-primary hover:underline'}`}
                        data-testid="otp-resend-button"
                      >
                        {otpCooldown > 0 ? `Resend in ${otpCooldown}s` : 'Resend code'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                <h2 className="font-heading text-xl font-semibold mb-1">Create Account</h2>
                <p className="text-sm text-muted-foreground mb-5">Sign up with Google, or use email and password below.</p>

                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">Setting up your account…</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-center" data-testid="google-signup-button">
                      <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error('Google sign-up cancelled.')}
                        size="large" width="320" text="signup_with" shape="rectangular" theme="outline" />
                    </div>

                    {/* Apple (web) */}
                    <Button type="button" variant="outline"
                      onClick={handleAppleSignIn}
                      className="w-full mt-2 h-[40px] border-slate-300 bg-black text-white hover:bg-black/90 hover:text-white"
                      data-testid="apple-signup-button">
                      <svg viewBox="0 0 384 512" className="h-4 w-4 mr-2 fill-current" aria-hidden="true">
                        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                      </svg>
                      Sign up with Apple
                    </Button>

                    <div className="flex items-center gap-2 my-5">
                      <Separator className="flex-1" />
                      <span className="text-xs text-muted-foreground">OR</span>
                      <Separator className="flex-1" />
                    </div>

                    {/* V14.5 Part 17 — email/password signup form. Adds a path
                        for users without a Google account (founder feedback:
                        "anyone without google account cannot sign up"). */}
                    <form onSubmit={handleEmailSignup} className="space-y-3" data-testid="email-signup-form">
                      <div>
                        <Label htmlFor="signup-name" className="text-xs uppercase tracking-wide text-muted-foreground">Full name</Label>
                        <Input id="signup-name" type="text" value={signupName}
                          onChange={(e) => setSignupName(e.target.value)}
                          placeholder="Priya Sharma"
                          autoComplete="name"
                          data-testid="signup-name-input" required />
                      </div>
                      <div>
                        <Label htmlFor="signup-email" className="text-xs uppercase tracking-wide text-muted-foreground">Email</Label>
                        <Input id="signup-email" type="email" value={signupEmail}
                          onChange={(e) => { setSignupEmail(e.target.value); if (emailIssue) setEmailIssue(null); }}
                          onBlur={(e) => checkSignupEmail(e.target.value)}
                          placeholder="you@example.com"
                          autoComplete="email"
                          className={emailIssue ? 'border-destructive focus-visible:ring-destructive' : undefined}
                          data-testid="signup-email-input" required />
                        {emailChecking && (
                          <p className="text-[11px] text-muted-foreground mt-1" data-testid="signup-email-checking">Checking this email…</p>
                        )}
                        {emailIssue && (
                          <div className="mt-1.5 rounded-md bg-destructive/10 border border-destructive/30 px-2.5 py-2" data-testid="signup-email-error">
                            <p className="text-[12px] text-destructive leading-snug">{emailIssue.message}</p>
                            {emailIssue.suggestion && (
                              <button type="button"
                                onClick={() => { setSignupEmail(emailIssue.suggestion); setEmailIssue(null); }}
                                className="mt-1.5 text-[12px] font-medium underline text-destructive"
                                data-testid="signup-email-suggestion-btn">
                                Use {emailIssue.suggestion}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="signup-phone" className="text-xs uppercase tracking-wide text-muted-foreground">Phone (with country code)</Label>
                        <Input id="signup-phone" type="tel" value={signupPhone}
                          onChange={(e) => setSignupPhone(e.target.value)}
                          placeholder="+91 98765 43210"
                          autoComplete="tel"
                          data-testid="signup-phone-input" />
                        <p className="text-[11px] text-muted-foreground mt-1">We'll WhatsApp a 6-digit code after your email is verified. Skip-able.</p>
                      </div>
                      <div>
                        <Label htmlFor="signup-password" className="text-xs uppercase tracking-wide text-muted-foreground">Password</Label>
                        <Input id="signup-password" type="password" value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          placeholder="At least 6 characters"
                          autoComplete="new-password"
                          minLength={6}
                          data-testid="signup-password-input" required />
                        <p className="text-[11px] text-muted-foreground mt-1">Minimum 6 characters. We'll email you a 6-digit code to verify your email before creating your account.</p>
                      </div>
                      <Button type="submit" className="w-full" data-testid="signup-submit-button">
                        <User className="h-4 w-4 mr-2" /> Create account
                      </Button>
                    </form>

                    <p className="text-xs text-muted-foreground text-center mt-4">
                      Already have an account?{' '}
                      <button type="button" onClick={() => setMode('signin')} className="text-primary hover:underline">Sign in</button>
                    </p>
                  </>
                )}
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
        <p className="text-[11px] text-muted-foreground text-center mt-4 px-4 leading-relaxed" data-testid="auth-terms-footer">
          By continuing, you agree to our{' '}
          <a href="https://www.soulsanskar.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Terms of Service</a>,{' '}
          <a href="https://www.soulsanskar.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Privacy Policy</a>{' '}and{' '}
          <a href="https://www.soulsanskar.com/refund-policy" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Refund Policy</a>.
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <LoginForm />
    </GoogleOAuthProvider>
  );
}
